/**
 * WorksheetLearning.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Probes active in all five modes (Odd One Out, Match, Sequence, Count, Diff).
 * Five probe types cycle per round/group/question.
 * One probe per item per turn. Same 3-button rating row.
 * Backend: `signals: [{ probe, level }]` added to PATCH /flashcard-sessions/:id/review.
 *
 * Probe targets (item shown as thumbnail in ProbeRound):
 *   OddOneOutMode  → item the child selected as odd (group[selected] || group[0])
 *   MatchMode      → item from the correct match (left-column item)
 *   SequenceMode   → item from correctOrder cycling by round
 *   CountMode      → current question item (q.item)
 *   DiffMode       → items[round % items.length]
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const WORKSHEET_TYPES = [
  { id:"odd-one-out",     label:"Odd One Out",          icon:"🔍", color:"#FF6B35", bg:"#FFF3EE", desc:"Tap the picture that doesn't belong" },
  { id:"match-following", label:"Match the Following",  icon:"🔗", color:"#2E86AB", bg:"#EEF6FB", desc:"Click image + label to match pairs" },
  { id:"sequence-order",  label:"Sequence & Ordering",  icon:"🔢", color:"#7B2D8B", bg:"#F5EEF8", desc:"Tap pictures in the correct order" },
  { id:"count-items",     label:"Count the Items",      icon:"🔢", color:"#1B8A5A", bg:"#EDF7F2", desc:"Pick the correct count from 4 options" },
  { id:"find-differences",label:"Find the Differences", icon:"👀", color:"#C0392B", bg:"#FDEDEC", desc:"Mark how many differences the child finds" },
];

const LANGUAGES = [
  { code:"en", name:"English",  voice:"en-IN" },
  { code:"ml", name:"മലയാളം", voice:"ml-IN" },
  { code:"hi", name:"हिंदी",   voice:"hi-IN" },
  { code:"ta", name:"தமிழ்",  voice:"ta-IN" },
  { code:"te", name:"తెలుగు", voice:"te-IN" },
];

// ─── Five probe types ─────────────────────────────────────────────────────────
// Cycle by round/group/question index (index % 5).
// Work on existing item labels + category — no per-item setup needed.
const PROBES = [
  {
    id: "identify",
    title: "Identify",
    question: (label) => `Can they identify "${label || "this item"}"?`,
    hint: "Point or tap when named — receptive identification",
  },
  {
    id: "name",
    title: "Name it",
    question: (label) => `Can they name it aloud? (expected: "${label || "the item"}")`,
    hint: "Spontaneous expressive naming — verbal, sign, or AAC",
  },
  {
    id: "attribute",
    title: "Attribute",
    question: () => "Can they describe one attribute of this item?",
    hint: "Color, size, shape, or texture",
  },
  {
    id: "category",
    title: "Category",
    question: (label, category) =>
      `What group does ${label || "this"} belong to?` +
      (category && category !== "Custom" ? ` (expected: ${category})` : ""),
    hint: "Fruit, animal, tool, vehicle… concept grouping",
  },
  {
    id: "function",
    title: "Function",
    question: (label) => `What is ${label || "this"} used for?`,
    hint: "Purpose, action, or functional understanding",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers) {
  const h = { "Content-Type":"application/json", ...(headers||{}) };
  const res = await fetch(`${API}${path}`, {
    method, headers:h,
    body: body ? JSON.stringify(body) : undefined,
  });
  const t = await res.text();
  return t ? JSON.parse(t) : {};
}

async function closeSession(sessionId, getAuthHeader, startMs) {
  if (!sessionId) return;
  try {
    await apiFetch(
      "PUT",
      `/flashcard-sessions/${sessionId}/close`,
      { durationMin: Math.round((Date.now() - (startMs || Date.now())) / 60000) },
      getAuthHeader()
    );
  } catch {}
}

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const lang = LANGUAGES.find(l => l.code === langCode) || LANGUAGES[0];
  const utt  = new SpeechSynthesisUtterance(text);
  utt.lang   = lang.voice; utt.rate = 0.85; utt.pitch = 1.1;
  window.speechSynthesis.speak(utt);
}

function shuffle(arr) {
  const a = [...arr];
  for (let i=a.length-1; i>0; i--) {
    const j = Math.floor(Math.random()*(i+1));
    [a[i],a[j]] = [a[j],a[i]];
  }
  return a;
}

function resolveUrl(item) {
  if (!item?.imageUrl) return "";
  const base = API.replace("/api","");
  if (item.imageUrl.startsWith("/uploads")) return `${base}${item.imageUrl}`;
  return item.imageUrl;
}

function getLabel(item, language) {
  return item?.labels?.[language] || item?.labels?.en || item?.name || "";
}

// ─── Shared UI ────────────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:60,flexDirection:"column",gap:12}}>
      <div style={{width:28,height:28,border:"3px solid #e0e7f0",borderTop:"3px solid #003366",borderRadius:"50%",animation:"spin .7s linear infinite"}}/>
      <div style={{fontSize:13,color:"#aaa"}}>Loading…</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function StarBurst({ count=3 }) {
  return (
    <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:900,overflow:"hidden"}}>
      {Array.from({length:count*8}).map((_,i) => {
        const x=Math.random()*100, dur=0.8+Math.random()*0.6, delay=Math.random()*0.4;
        const color=["#FFD700","#FF6B35","#1B6B45","#003366","#7B2D8B","#E65100"][i%6];
        return <div key={i} style={{position:"absolute",left:`${x}%`,top:"60%",width:10,height:10,borderRadius:"50%",background:color,animation:`ws_burst${i%2===0?"Up":"Diag"} ${dur}s ease-out ${delay}s forwards`,opacity:0}}/>;
      })}
      <style>{`@keyframes ws_burstUp{0%{opacity:1;transform:translateY(0) scale(1)}100%{opacity:0;transform:translateY(-300px) scale(.4)}}@keyframes ws_burstDiag{0%{opacity:1;transform:translate(0,0) scale(1)}100%{opacity:0;transform:translate(110px,-260px) scale(.3)}}`}</style>
    </div>
  );
}

function CelebrationScreen({ correct, total, childName, worksheetTitle, onReplay, onExit }) {
  const pct   = total > 0 ? Math.round((correct/total)*100) : 0;
  const stars = pct >= 85 ? 3 : pct >= 60 ? 2 : 1;
  const msgs  = ["Keep practising — you'll get there! 💪","Good effort! Try again to improve 🌟","Great job! Well done 🎉"];
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <StarBurst count={stars}/>
      <div style={{background:"white",borderRadius:24,padding:"40px 32px",maxWidth:440,width:"100%",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.4)",position:"relative",zIndex:10}}>
        <div style={{fontSize:48,marginBottom:8,letterSpacing:4}}>{Array.from({length:3}).map((_,i)=><span key={i} style={{opacity:i<stars?1:0.2}}>⭐</span>)}</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:"#003366",marginBottom:4}}>{stars===3?"Amazing!":stars===2?"Well done!":"Good try!"}</div>
        {childName     && <div style={{fontSize:14,color:"#888",marginBottom:2}}>{childName}</div>}
        {worksheetTitle && <div style={{fontSize:11,color:"#aaa",marginBottom:16}}>{worksheetTitle}</div>}
        <div style={{fontSize:13,color:"#555",marginBottom:28,lineHeight:1.6}}>{msgs[stars-1]}</div>
        <div style={{display:"flex",justifyContent:"center",gap:20,marginBottom:28}}>
          {[{label:"Score",val:`${pct}%`,color:pct>=85?"#1B6B45":pct>=60?"#D4AC0D":"#C0392B"},{label:"Correct",val:correct,color:"#1B6B45"},{label:"Total",val:total,color:"#003366"}].map(s=>(
            <div key={s.label} style={{textAlign:"center"}}>
              <div style={{fontSize:28,fontWeight:900,color:s.color}}>{s.val}</div>
              <div style={{fontSize:10,color:"#aaa",fontWeight:600,textTransform:"uppercase"}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {onReplay && <button onClick={onReplay} style={{padding:"13px",background:"#003366",color:"white",border:"none",borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,51,102,.3)"}}>🔄 Play Again</button>}
          <button onClick={onExit} style={{padding:"13px",background:"white",color:"#666",border:"1.5px solid #dce3f5",borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer"}}>← Back to Setup</button>
        </div>
      </div>
    </div>
  );
}

function SessionNotesScreen({ sessionId, getAuthHeader, startMs, onDone, childName, score }) {
  const [notes,setNotes]=useState(""); const [gen,setGen]=useState(""); const [saving,setSaving]=useState(false);
  const scoreColor=score>=85?"#1B6B45":score>=60?"#D4AC0D":"#C0392B";
  const scoreBg   =score>=85?"#EDF7F2":score>=60?"#FEF9E7":"#FDEDEC";
  async function save(){
    setSaving(true);
    try{ await apiFetch("PUT",`/flashcard-sessions/${sessionId}/close`,{durationMin:Math.round((Date.now()-(startMs||Date.now()))/60000),sessionNotes:notes,generalizationNote:gen},getAuthHeader()); }catch{}
    setSaving(false); onDone();
  }
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{background:"white",borderRadius:22,padding:"34px 30px",width:500,maxWidth:"96vw",boxShadow:"0 24px 80px rgba(0,0,0,.4)"}}>
        <div style={{textAlign:"center",marginBottom:26}}>
          <div style={{fontSize:40,marginBottom:10}}>📝</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,color:"#1a1a2e",marginBottom:8}}>Session Notes</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
            {childName&&<span style={{fontSize:13,color:"#666",fontWeight:600}}>{childName}</span>}
            {childName&&<span style={{color:"#ddd"}}>·</span>}
            <span style={{fontSize:13,fontWeight:800,color:scoreColor,background:scoreBg,padding:"2px 12px",borderRadius:20,border:`1px solid ${scoreColor}40`}}>{score}% score</span>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Session Observations</div>
          <textarea autoFocus value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Attention span, engagement, behaviour, fatigue, prompts needed…"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"} onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{marginBottom:26}}>
          <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Generalization Note <span style={{fontWeight:400,textTransform:"none",letterSpacing:0}}>(optional)</span></div>
          <textarea value={gen} onChange={e=>setGen(e.target.value)} rows={2} placeholder="Did the child apply any learned concepts outside of this session?"
            style={{width:"100%",border:"1.5px solid #dce3f5",borderRadius:10,padding:"10px 13px",fontSize:14,outline:"none",resize:"vertical",boxSizing:"border-box",fontFamily:"inherit",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor="#003366"} onBlur={e=>e.target.style.borderColor="#dce3f5"}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onDone} style={{flex:1,padding:"12px",background:"white",border:"1.5px solid #dce3f5",borderRadius:11,color:"#666",fontWeight:700,fontSize:14,cursor:"pointer"}}>Skip</button>
          <button onClick={save} disabled={saving} style={{flex:2,padding:"12px",background:saving?"#aaa":"#003366",color:"white",border:"none",borderRadius:11,fontWeight:700,fontSize:14,cursor:saving?"default":"pointer",boxShadow:saving?"none":"0 4px 16px rgba(0,51,102,.3)"}}>
            {saving?"Saving…":"✓ Save & Exit"}
          </button>
        </div>
        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"#aaa"}}>Notes saved to child's session log in Progress Tracker</div>
      </div>
    </div>
  );
}

function ModeBar({ icon, title, childName, current, total, color="#003366", onExit }) {
  const pct = total > 0 ? (current/total)*100 : 0;
  return (
    <>
      <div style={{background:"white",borderBottom:"1px solid #dce3f5",padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button onClick={onExit} style={{background:"#f0f4ff",border:"none",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:12,color:"#666",fontWeight:700}}>← Exit</button>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:13,color}}>{icon} {title}</div>
          {childName && <div style={{fontSize:11,color:"#888"}}>{childName}</div>}
        </div>
        {total>0 && <div style={{fontSize:12,color:"#888",fontWeight:600}}>{current} / {total}</div>}
      </div>
      <div style={{height:5,background:"#dce3f5",flexShrink:0}}>
        <div style={{width:`${pct}%`,height:"100%",background:color,transition:"width .4s"}}/>
      </div>
    </>
  );
}

function TeacherRating({ onRate, disabled, label="How did the child respond?" }) {
  return (
    <div style={{marginTop:20,width:"min(480px,92vw)"}}>
      <div style={{fontSize:11,fontWeight:700,color:"#888",textAlign:"center",textTransform:"uppercase",letterSpacing:0.8,marginBottom:12}}>{label}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
        {[{q:1,label:"Needs Help",emoji:"🔴",color:"#C0392B",bg:"#FDEDEC"},{q:3,label:"With Prompt",emoji:"🟡",color:"#D4AC0D",bg:"#FEF9E7"},{q:5,label:"Got It! ✓",emoji:"🟢",color:"#1B6B45",bg:"#EDF7F2"}].map(r=>(
          <button key={r.q} onClick={()=>onRate(r.q)} disabled={disabled}
            style={{padding:"18px 8px",background:r.bg,border:`2.5px solid ${r.color}`,borderRadius:16,cursor:"pointer",fontWeight:800,fontSize:13,color:r.color,display:"flex",flexDirection:"column",alignItems:"center",gap:6,opacity:disabled?0.5:1,minHeight:72}}>
            <span style={{fontSize:28}}>{r.emoji}</span>{r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── ProbeRound ───────────────────────────────────────────────────────────────
// Shared by all five worksheet modes.
// probe = PROBES[roundIndex % PROBES.length]
// item  = the worksheet item being probed (shown as thumbnail)
// onRate("absent"|"emerging"|"strong")
function ProbeRound({ probe, item, language, onRate, submitting }) {
  const label    = getLabel(item, language);
  const category = item?.category || "";
  const question = probe.id === "category"
    ? probe.question(label, category)
    : probe.question(label);

  return (
    <div style={{marginTop:16, width:"min(480px,92vw)"}}>
      <div style={{background:"#EBF4FB", borderRadius:12, padding:"13px 16px",
                   border:"1.5px solid #BDD7EE", marginBottom:14,
                   display:"flex", alignItems:"center", gap:14}}>
        {item?.imageUrl && (
          <div style={{width:52, height:52, borderRadius:10, overflow:"hidden",
                       border:"2px solid #BDD7EE", flexShrink:0,
                       background:"#f0f7ff", display:"flex", alignItems:"center", justifyContent:"center"}}>
            <img src={resolveUrl(item)} alt={label}
              style={{maxWidth:"100%", maxHeight:"100%", objectFit:"contain"}}/>
          </div>
        )}
        <div style={{flex:1}}>
          <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:5}}>
            <span style={{fontSize:10, fontWeight:800, color:"#1A4A6E", letterSpacing:.8,
                           textTransform:"uppercase", background:"#BDD7EE",
                           padding:"2px 8px", borderRadius:6}}>
              {probe.title}
            </span>
          </div>
          <div style={{fontWeight:700, fontSize:13, color:"#003366", lineHeight:1.5, marginBottom:3}}>
            {question}
          </div>
          <div style={{fontSize:11, color:"#5C7A9E", fontStyle:"italic"}}>
            {probe.hint}
          </div>
        </div>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10}}>
        {[
          { level:"absent",   label:"Needs Help",  emoji:"🔴", color:"#C0392B", bg:"#FDEDEC" },
          { level:"emerging", label:"With Prompt", emoji:"🟡", color:"#D4AC0D", bg:"#FEF9E7" },
          { level:"strong",   label:"Got It! ✓",   emoji:"🟢", color:"#1B6B45", bg:"#EDF7F2" },
        ].map(r => (
          <button key={r.level} onClick={() => onRate(r.level)} disabled={submitting}
            style={{padding:"16px 8px", background:r.bg, border:`2.5px solid ${r.color}`,
                     borderRadius:16, cursor:"pointer", fontWeight:800, fontSize:13,
                     color:r.color, display:"flex", flexDirection:"column",
                     alignItems:"center", gap:6, opacity:submitting ? 0.5 : 1, minHeight:70}}>
            <span style={{fontSize:26}}>{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 1 — ODD ONE OUT  (with probe after each group rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per group:
//   1. Grid of items shown → child taps the odd one out → selection highlighted
//   2. Teacher rates quality (phase: "rate") → stores quality, moves to "probe"
//   3. ProbeRound shown for the selected item (or group[0] as fallback)
//   4. Teacher rates probe → both posted with signals[], advance to next group
//
function OddOneOutMode({ items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone }) {
  const groups = [];
  for (let i=0; i<items.length; i+=6) groups.push(items.slice(i,i+6));
  if (!groups.length) return null;

  const wt           = WORKSHEET_TYPES[0];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submitting   = useRef(false);

  const [showNotes,      setShowNotes]      = useState(false);
  const [groupIdx,       setGroupIdx]       = useState(0);
  const [selected,       setSelected]       = useState(null);
  const [history,        setHistory]        = useState([]);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const startMs = useRef(Date.now());

  const done  = groupIdx >= groups.length;
  const group = groups[groupIdx] || [];
  const probe = PROBES[groupIdx % PROBES.length];
  // Probe targets the item the child selected, falling back to first item
  const probeItem = group[selected !== null ? selected : 0];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const handleTap = (idx) => {
    if (selected !== null || phase !== "rate") return;
    setSelected(idx);
    speak(getLabel(group[idx], language), language);
  };

  const handleQualityRate = (quality) => {
    if (submitting.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submitting.current || pendingQuality === null) return;
    submitting.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     `group-${groupIdx}`,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submitting.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setSelected(null);
      setGroupIdx(g => g + 1);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = groups.length>0?Math.round((correct/groups.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={groups.length} childName={childName} worksheetTitle={worksheetTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setGroupIdx(0); setSelected(null); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  const cols = Math.min(group.length, 3);

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={wt.icon} title="Odd One Out" childName={childName} color={wt.color} current={groupIdx+1} total={groups.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,gap:16}}>

        {phase === "rate" && (
          <>
            <div style={{background:wt.bg,border:`1.5px solid ${wt.color}40`,borderRadius:10,padding:"9px 18px",fontSize:13,color:wt.color,fontWeight:700,maxWidth:480,textAlign:"center"}}>
              Which picture does <strong>NOT</strong> belong with the others?
            </div>
            <div style={{fontSize:11,color:"#aaa",fontWeight:700,textTransform:"uppercase",letterSpacing:0.8}}>
              Group {groupIdx+1} of {groups.length}
            </div>
            <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:12,width:"min(520px,94vw)"}}>
              {group.map((item,i) => {
                const isSel = selected===i;
                return (
                  <div key={i} onClick={()=>handleTap(i)}
                    style={{border:`2.5px solid ${isSel?wt.color:"#dce3f5"}`,borderRadius:14,overflow:"hidden",cursor:"pointer",background:isSel?wt.bg:"white",boxShadow:isSel?`0 6px 24px ${wt.color}44`:"0 2px 8px rgba(0,0,0,.06)",transition:"all .2s",display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
                    {isSel && <div style={{position:"absolute",top:6,right:6,background:wt.color,color:"white",borderRadius:"50%",width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:900,zIndex:2}}>✓</div>}
                    <div style={{width:"100%",height:110,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f9ff",padding:10}}>
                      <img src={resolveUrl(item)} alt={getLabel(item,language)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                    </div>
                    <div style={{padding:"7px 10px",fontSize:10,fontWeight:700,color:isSel?wt.color:"#333",textAlign:"center",width:"100%"}}>
                      {getLabel(item,language)}
                    </div>
                  </div>
                );
              })}
            </div>
            {selected !== null
              ? <TeacherRating onRate={handleQualityRate} disabled={submitting.current}/>
              : <div style={{fontSize:13,color:"#aaa",fontWeight:600,marginTop:8}}>👆 Child taps the picture that doesn't belong</div>
            }
          </>
        )}

        {phase === "probe" && (
          <>
            <div style={{fontSize:11,color:wt.color,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>
              ✓ Rated — now one more question:
            </div>
            <ProbeRound
              probe={probe}
              item={probeItem}
              language={language}
              onRate={handleProbeRate}
              submitting={submitting.current}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 2 — MATCH THE FOLLOWING  (with probe after each correct match)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per correct match:
//   1. Child picks left + right → match detected → tiles flip to matched state
//   2. Probe panel slides up from bottom for the matched item
//   3. Teacher rates probe → review posted with signals[], panel dismisses
//   Wrong matches: posted immediately without probe (same as before)
//
function MatchMode({ items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone }) {
  const wt           = WORKSHEET_TYPES[1];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const pendingMatchData = useRef(null); // stored on match, posted after probe
  const matchCountRef    = useRef(0);   // for probe cycling

  const buildState = () => {
    const pool  = items.slice(0, 6);
    const left  = pool.map((item,i) => ({key:`L${i}`, matchId:i, item, matched:false, selected:false}));
    const right = shuffle(pool.map((item,i) => ({key:`R${i}`, matchId:i, item, matched:false, selected:false})));
    return {left, right};
  };

  const [showNotes,       setShowNotes]       = useState(false);
  const initial                               = useRef(buildState());
  const [left,  setLeft]                      = useState(initial.current.left);
  const [right, setRight]                     = useState(initial.current.right);
  const [selL,  setSelL]                      = useState(null);
  const [selR,  setSelR]                      = useState(null);
  const [flash, setFlash]                     = useState(null);
  const [history, setHistory]                 = useState([]);
  const [locking, setLocking]                 = useState(false);
  const [probeCard, setProbeCard]             = useState(null); // triggers probe panel
  const [probeSubmitting, setProbeSubmitting] = useState(false);

  const totalPairs = left.length;
  const matched    = left.filter(l=>l.matched).length;
  const done       = matched >= totalPairs && !probeCard;

  useEffect(() => {
    if (matched >= totalPairs && !probeCard && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [matched, probeCard]);

  useEffect(()=>{
    if (selL===null || selR===null || locking || probeCard) return;
    setLocking(true);
    const isMatch = selL===selR;
    setFlash({matchId:selL, ok:isMatch});

    if (isMatch) {
      speak(getLabel(left.find(l=>l.matchId===selL)?.item, language), language);
      setLeft(p  => p.map(l => l.matchId===selL ? {...l, matched:true, selected:false} : l));
      setRight(p => p.map(r => r.matchId===selR ? {...r, matched:true, selected:false} : r));
      setHistory(h=>[...h,{correct:true}]);

      const matchedItem = left.find(l=>l.matchId===selL)?.item;
      pendingMatchData.current = { cardId:`match-${selL}`, quality:5, responseMs:null, prompted:false };
      matchCountRef.current += 1;

      // After flash, show probe instead of immediately clearing
      setTimeout(()=>{
        setFlash(null);
        setLocking(false);
        setProbeCard(matchedItem || null);
      }, 600);
    } else {
      // Wrong: post immediately, no probe
      setHistory(h=>[...h,{correct:false}]);
      (async()=>{
        if (!submRef.current && sessionId) {
          submRef.current = true;
          try {
            await apiFetch("PATCH",`/flashcard-sessions/${sessionId}/review`,{
              cardId:`match-${selL}`, quality:1, responseMs:null, prompted:false,
            }, getAuthHeader());
          } catch {}
          submRef.current = false;
        }
      })();
      setTimeout(()=>{
        setFlash(null); setSelL(null); setSelR(null);
        setLeft(p  => p.map(l=>({...l, selected:false})));
        setRight(p => p.map(r=>({...r, selected:false})));
        setLocking(false);
      }, 1000);
    }
  },[selL,selR]);

  const handleProbeRate = async (probeLevel) => {
    if (probeSubmitting || !probeCard) return;
    setProbeSubmitting(true);
    const probeIdx = (matchCountRef.current - 1) % PROBES.length;
    const probe = PROBES[probeIdx];
    try {
      if (sessionId && pendingMatchData.current) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          ...pendingMatchData.current,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    pendingMatchData.current = null;
    setProbeSubmitting(false);
    setProbeCard(null);
    setSelL(null); setSelR(null);
  };

  const pickLeft  = (matchId) => {
    if (locking || probeCard || left.find(l=>l.matchId===matchId)?.matched) return;
    setSelL(m => m===matchId ? null : matchId);
    setLeft(p => p.map(l=>({...l, selected:l.matchId===matchId ? !l.selected : l.selected})));
  };
  const pickRight = (matchId) => {
    if (locking || probeCard || right.find(r=>r.matchId===matchId)?.matched) return;
    setSelR(m => m===matchId ? null : matchId);
    setRight(p => p.map(r=>({...r, selected:r.matchId===matchId ? !r.selected : r.selected})));
  };

  const replay = () => {
    closedRef.current=false; sessionStart.current=Date.now(); matchCountRef.current=0;
    const s=buildState(); setLeft(s.left); setRight(s.right); setSelL(null); setSelR(null);
    setFlash(null); setHistory([]); setLocking(false); setProbeCard(null); setShowNotes(false);
  };

  if (done) {
    const correct = history.filter(h => h.correct).length;
    const total   = history.length;
    const score   = total > 0 ? Math.round((correct / total) * 100) : 100;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return <CelebrationScreen correct={correct} total={total} childName={childName} worksheetTitle={worksheetTitle} onReplay={replay} onExit={()=>setShowNotes(true)}/>;
  }

  const Card = ({side, matchId, item, isSelected, isMatched}) => {
    const isFlashOk  = flash?.matchId===matchId && flash?.ok;
    const isFlashBad = flash?.matchId===matchId && !flash?.ok;
    let border="2.5px solid #dce3f5", bg="white";
    if (isMatched)       { border="2.5px solid #1B6B45"; bg="#EDF7F2"; }
    else if (isFlashOk)  { border="2.5px solid #1B6B45"; bg="#EDF7F2"; }
    else if (isFlashBad) { border="2.5px solid #C0392B"; bg="#FDEDEC"; }
    else if (isSelected) { border=`2.5px solid ${wt.color}`; bg=wt.bg; }
    return (
      <div onClick={()=>side==="left"?pickLeft(matchId):pickRight(matchId)}
        style={{border,borderRadius:12,overflow:"hidden",cursor:isMatched?"default":"pointer",background:bg,transition:"all .2s",boxShadow:"0 2px 8px rgba(0,0,0,.06)",display:"flex",flexDirection:"column",alignItems:"center"}}>
        <div style={{width:"100%",height:88,display:"flex",alignItems:"center",justifyContent:"center",background:isMatched?"#EDF7F2":"#f8f9ff",padding:8}}>
          {isMatched
            ? <div style={{fontSize:28}}>✓</div>
            : <img src={resolveUrl(item)} alt={getLabel(item,language)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
          }
        </div>
        <div style={{padding:"5px 8px",fontSize:10,fontWeight:700,color:isMatched?"#1B6B45":isSelected?wt.color:"#333",textAlign:"center",borderTop:"1px solid #f0f4f8",width:"100%"}}>
          {getLabel(item,language)}
        </div>
      </div>
    );
  };

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={wt.icon} title="Match the Following" childName={childName} color={wt.color} current={matched} total={totalPairs} onExit={onDone}/>

      {/* Main grid — dimmed while probe is showing */}
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"20px 16px",gap:12,opacity:probeCard?0.3:1,transition:"opacity .25s",pointerEvents:probeCard?"none":"auto"}}>
        <div style={{fontSize:13,color:"#8a9ab5",fontWeight:600}}>Select one from each column to match them</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 40px 1fr",gap:10,width:"min(560px,96vw)",alignItems:"center"}}>
          <div style={{fontSize:10,fontWeight:800,color:wt.color,textTransform:"uppercase",letterSpacing:0.8,textAlign:"center"}}>Column A</div>
          <div/>
          <div style={{fontSize:10,fontWeight:800,color:"#8a9ab5",textTransform:"uppercase",letterSpacing:0.8,textAlign:"center"}}>Column B</div>
          {left.map((lp,i) => {
            const rp = right[i];
            return [
              <Card key={lp.key} side="left"  matchId={lp.matchId} item={lp.item} isSelected={selL===lp.matchId} isMatched={lp.matched}/>,
              <div key={`m${i}`} style={{display:"flex",alignItems:"center",justifyContent:"center"}}>
                <div style={{width:28,height:28,border:"1.5px dashed #ccc",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#ddd"}}>✦</div>
              </div>,
              <Card key={rp.key} side="right" matchId={rp.matchId} item={rp.item} isSelected={selR===rp.matchId} isMatched={rp.matched}/>,
            ];
          })}
        </div>
        <div style={{fontSize:11,color:"#aaa",fontWeight:600}}>{matched} of {totalPairs} matched</div>
      </div>

      {/* Probe panel — slides up after each correct match */}
      {probeCard && (
        <div style={{position:"fixed",bottom:0,left:0,right:0,
                     background:"white",borderTop:`2.5px solid ${wt.color}`,
                     borderRadius:"20px 20px 0 0",padding:"20px 24px 32px",
                     boxShadow:"0 -8px 40px rgba(0,0,0,.18)",
                     display:"flex",flexDirection:"column",alignItems:"center",
                     animation:"slideUp .28s cubic-bezier(.22,.68,0,1.2) both",zIndex:50}}>
          <div style={{fontSize:9,fontWeight:800,color:wt.color,textTransform:"uppercase",
                       letterSpacing:.8,marginBottom:6,alignSelf:"flex-start"}}>
            ✓ Matched! — one more question:
          </div>
          <ProbeRound
            probe={PROBES[(matchCountRef.current - 1) % PROBES.length]}
            item={probeCard}
            language={language}
            onRate={handleProbeRate}
            submitting={probeSubmitting}
          />
        </div>
      )}
      <style>{`@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 3 — SEQUENCE & ORDERING  (with probe after each round rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per round:
//   1. Shuffled items shown → child taps in order → Submit Order button
//   2. Correct order revealed + TeacherRating shown (phase: "rate")
//   3. Teacher rates → stores quality, moves to "probe" phase
//   4. ProbeRound shown for one item cycling through correctOrder
//   5. Teacher rates probe → both posted with signals[], advance to next round
//
function SequenceMode({ items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone }) {
  const wt           = WORKSHEET_TYPES[2];
  const correctOrder = useRef(items.slice(0,6));
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const ROUNDS       = 3;

  const [showNotes,      setShowNotes]      = useState(false);
  const [shuffled,       setShuffled]       = useState(()=>shuffle([...correctOrder.current]));
  const [tapped,         setTapped]         = useState([]);
  const [submitted,      setSubmitted]      = useState(false);
  const [history,        setHistory]        = useState([]);
  const [round,          setRound]          = useState(0);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const startMs = useRef(Date.now());

  const total = correctOrder.current.length;
  const done  = round >= ROUNDS;
  const probe = PROBES[round % PROBES.length];
  // Probe targets an item from the correct order cycling by round
  const probeItem = correctOrder.current[round % correctOrder.current.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const handleTap = (idx) => {
    if (submitted || phase !== "rate") return;
    setTapped(p => p.includes(idx) ? p.filter(i=>i!==idx) : [...p,idx]);
  };

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     `seq-round-${round}`,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setShuffled(shuffle([...correctOrder.current]));
      setTapped([]); setSubmitted(false);
      setRound(r => r + 1);
      setPhase("rate"); setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = ROUNDS>0?Math.round((correct/ROUNDS)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={ROUNDS} childName={childName} worksheetTitle={worksheetTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setRound(0); setTapped([]); setSubmitted(false); setHistory([]); setShuffled(shuffle([...correctOrder.current])); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  const cols = Math.min(total,4);

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={wt.icon} title="Sequence & Ordering" childName={childName} color={wt.color} current={round+1} total={ROUNDS} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,gap:16}}>

        {phase === "rate" && (
          <>
            <div style={{background:wt.bg,border:`1.5px solid ${wt.color}30`,borderRadius:10,padding:"9px 18px",fontSize:13,color:wt.color,fontWeight:700,textAlign:"center"}}>
              Tap the pictures in the correct order  1 → {total}
            </div>
            <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:12,width:"min(540px,94vw)"}}>
              {shuffled.map((item,i)=>{
                const pos=tapped.indexOf(i); const isTapped=pos!==-1;
                return (
                  <div key={i} onClick={()=>handleTap(i)}
                    style={{border:`2.5px solid ${isTapped?wt.color:"#dce3f5"}`,borderRadius:14,overflow:"hidden",cursor:"pointer",background:isTapped?wt.bg:"white",boxShadow:isTapped?`0 4px 16px ${wt.color}33`:"0 2px 8px rgba(0,0,0,.05)",transition:"all .2s",display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
                    <div style={{position:"absolute",top:6,right:6,width:24,height:24,borderRadius:"50%",background:isTapped?wt.color:"#e2e8f0",color:isTapped?"white":"#aaa",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:900,zIndex:2}}>
                      {isTapped?pos+1:""}
                    </div>
                    <div style={{width:"100%",height:100,display:"flex",alignItems:"center",justifyContent:"center",background:"#f8f9ff",padding:10}}>
                      <img src={resolveUrl(item)} alt={getLabel(item,language)} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain"}}/>
                    </div>
                    <div style={{padding:"6px 10px",fontSize:10,fontWeight:700,color:isTapped?wt.color:"#333",textAlign:"center",width:"100%"}}>{getLabel(item,language)}</div>
                  </div>
                );
              })}
            </div>
            {tapped.length>0 && (
              <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",justifyContent:"center"}}>
                {tapped.map((idx,pos)=>(
                  <div key={pos} style={{display:"flex",alignItems:"center",gap:4}}>
                    <div style={{background:wt.color,color:"white",borderRadius:8,padding:"3px 10px",fontSize:11,fontWeight:800}}>{pos+1}. {getLabel(shuffled[idx],language)}</div>
                    {pos<tapped.length-1 && <span style={{color:"#aaa",fontSize:12}}>→</span>}
                  </div>
                ))}
              </div>
            )}
            {tapped.length===total && !submitted && (
              <button onClick={()=>setSubmitted(true)} style={{padding:"12px 36px",background:wt.color,color:"white",border:"none",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer",boxShadow:`0 4px 16px ${wt.color}44`}}>
                Submit Order →
              </button>
            )}
            {submitted && (
              <>
                <div style={{background:"#f0f4ff",borderRadius:10,padding:"10px 16px",fontSize:12,color:"#1A4A6E",fontWeight:600,textAlign:"center"}}>
                  📋 Correct order: {correctOrder.current.map(i=>getLabel(i,language)).join(" → ")}
                </div>
                <TeacherRating onRate={handleQualityRate} disabled={submRef.current}/>
              </>
            )}
            {tapped.length<total && !submitted && (
              <div style={{fontSize:12,color:"#aaa",fontWeight:600}}>
                {tapped.length}/{total} tapped
                {tapped.length>0 && <button onClick={()=>setTapped([])} style={{marginLeft:12,background:"none",border:"none",color:"#aaa",fontSize:11,cursor:"pointer",textDecoration:"underline"}}>Reset</button>}
              </div>
            )}
          </>
        )}

        {phase === "probe" && (
          <>
            <div style={{fontSize:11,color:wt.color,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>
              ✓ Rated — one more question:
            </div>
            <ProbeRound
              probe={probe}
              item={probeItem}
              language={language}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 4 — COUNT THE ITEMS  (with probe after each answer)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per question:
//   1. Item grid shown + 4 count options → child picks
//   2. Outcome shown (correct/wrong) + review stored
//   3. 700ms → probe question shown (item stays visible above)
//   4. Teacher rates probe → review posted with signals[], advance
//
function CountMode({ items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone }) {
  const wt           = WORKSHEET_TYPES[3];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const pendingReview = useRef(null); // stored on choose, posted after probe

  const questions = useRef(
    items.slice(0,6).map((item,i) => ({ item, count:(i%5)+2 }))
  );

  const buildOptions = (count) => {
    const opts = new Set([count]);
    while(opts.size<4) opts.add(Math.max(1, count + Math.floor(Math.random()*5)-2));
    return shuffle([...opts]);
  };

  const [showNotes,       setShowNotes]       = useState(false);
  const [qIdx,    setQIdx]    = useState(0);
  const [options, setOptions] = useState(()=>buildOptions(questions.current[0]?.count||3));
  const [chosen,  setChosen]  = useState(null);
  const [history, setHistory] = useState([]);
  const [showProbe, setShowProbe]             = useState(false);
  const [probeSubmitting, setProbeSubmitting] = useState(false);
  const startMs = useRef(Date.now());

  const q    = questions.current[qIdx];
  const done = qIdx >= questions.current.length && !showProbe;
  const probe = PROBES[qIdx % PROBES.length];

  useEffect(() => {
    if (qIdx >= questions.current.length && !showProbe && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [qIdx, showProbe]);

  const choose = (opt) => {
    if (chosen!==null || submRef.current || showProbe) return;
    setChosen(opt);
    const isCorrect = opt===q.count;
    speak(isCorrect ? getLabel(q.item,language) : "Try again", language);
    setHistory(h=>[...h,{correct:isCorrect}]);

    // Store review — posted after probe
    pendingReview.current = {
      cardId:     `count-${qIdx}`,
      quality:    isCorrect ? 5 : 1,
      responseMs: Date.now() - startMs.current,
      prompted:   false,
    };
    // After outcome pause → show probe
    setTimeout(() => setShowProbe(true), 700);
  };

  const handleProbeRate = async (probeLevel) => {
    if (probeSubmitting) return;
    setProbeSubmitting(true);
    try {
      if (sessionId && pendingReview.current) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          ...pendingReview.current,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    pendingReview.current = null;
    setProbeSubmitting(false);
    setShowProbe(false);
    const next = qIdx + 1;
    setQIdx(next);
    setChosen(null);
    if (next < questions.current.length) {
      setOptions(buildOptions(questions.current[next].count));
    }
    startMs.current = Date.now();
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = questions.current.length>0?Math.round((correct/questions.current.length)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={questions.current.length} childName={childName} worksheetTitle={worksheetTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setQIdx(0); setChosen(null); setHistory([]); setOptions(buildOptions(questions.current[0].count)); setShowProbe(false); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  if (qIdx >= questions.current.length) return null; // safety guard while probe dismisses

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={wt.icon} title="Count the Items" childName={childName} color={wt.color} current={qIdx+1} total={questions.current.length} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,gap:20}}>

        {/* Question + options — dimmed while probe is showing */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:20,width:"100%",opacity:showProbe?0.3:1,transition:"opacity .25s",pointerEvents:showProbe?"none":"auto"}}>
          <div style={{fontSize:15,fontWeight:700,color:"#555",textAlign:"center"}}>
            How many <span style={{color:wt.color,fontWeight:900}}>{getLabel(q.item,language)}</span> are there?
          </div>
          <div style={{background:"white",border:`2.5px solid ${wt.color}`,borderRadius:16,padding:20,display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center",alignItems:"center",width:"min(400px,92vw)",minHeight:140,boxShadow:`0 4px 24px ${wt.color}22`}}>
            {Array.from({length:q.count}).map((_,i) => (
              <img key={i} src={resolveUrl(q.item)} alt="" style={{width:56,height:56,objectFit:"contain"}}/>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,width:"min(380px,92vw)"}}>
            {options.map((opt,i) => {
              const isChosen=chosen===opt; const isCorrect=opt===q.count;
              let border="2.5px solid #dce3f5", bg="white", color="#1a2b49";
              if (chosen!==null) {
                if (isCorrect)    { border="2.5px solid #1B6B45"; bg="#EDF7F2"; color="#1B6B45"; }
                else if (isChosen){ border="2.5px solid #C0392B"; bg="#FDEDEC"; color="#C0392B"; }
              }
              return (
                <button key={i} onClick={()=>choose(opt)} disabled={chosen!==null}
                  style={{padding:"20px",background:bg,border,borderRadius:14,cursor:chosen!==null?"default":"pointer",fontSize:30,fontWeight:900,color,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,.06)",transition:"all .2s"}}>
                  {opt}
                </button>
              );
            })}
          </div>
          {chosen!==null && !showProbe && (
            <div style={{fontSize:16,fontWeight:800,color:chosen===q.count?"#1B6B45":"#C0392B",animation:"fadeUp .25s ease"}}>
              {chosen===q.count?`✅ Correct! There ${q.count===1?"is":"are"} ${q.count}.`:`❌ The answer is ${q.count}.`}
            </div>
          )}
        </div>

        {/* Probe panel — slides up from bottom after outcome */}
        {showProbe && (
          <div style={{position:"fixed",bottom:0,left:0,right:0,
                       background:"white",borderTop:`2.5px solid ${wt.color}`,
                       borderRadius:"20px 20px 0 0",padding:"20px 24px 32px",
                       boxShadow:"0 -8px 40px rgba(0,0,0,.18)",
                       display:"flex",flexDirection:"column",alignItems:"center",
                       animation:"slideUp .28s cubic-bezier(.22,.68,0,1.2) both",zIndex:50}}>
            <div style={{fontSize:9,fontWeight:800,color:wt.color,textTransform:"uppercase",letterSpacing:.8,marginBottom:6,alignSelf:"flex-start"}}>
              {chosen===q.count?"✅ Correct!":"❌ Not quite"} — one more question:
            </div>
            <ProbeRound
              probe={probe}
              item={q.item}
              language={language}
              onRate={handleProbeRate}
              submitting={probeSubmitting}
            />
          </div>
        )}
      </div>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 5 — FIND THE DIFFERENCES  (with probe after each round rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per round:
//   1. Two side-by-side images shown → teacher counts differences the child finds
//   2. Child submits count → TeacherRating shown (phase: "rate")
//   3. Teacher rates → stores quality, moves to "probe" phase
//   4. ProbeRound shown for cycling item
//   5. Teacher rates probe → both posted with signals[], advance to next round
//
function DiffMode({ items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone }) {
  const wt           = WORKSHEET_TYPES[4];
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const submRef      = useRef(false);
  const ROUNDS       = 2;

  const [showNotes,      setShowNotes]      = useState(false);
  const [found,          setFound]          = useState(0);
  const [submitted,      setSubmitted]      = useState(false);
  const [history,        setHistory]        = useState([]);
  const [round,          setRound]          = useState(0);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);
  const startMs = useRef(Date.now());

  const done = round >= ROUNDS;
  const probe = PROBES[round % PROBES.length];
  // Probe targets an item cycling through available items (may have 1-6)
  const probeItem = items[round % Math.max(items.length, 1)];
  const item      = items[0]; // main difference image (always first)

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const handleQualityRate = (quality) => {
    if (submRef.current) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submRef.current || pendingQuality === null) return;
    submRef.current = true;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:     `diff-round-${round}`,
          quality:    pendingQuality,
          responseMs: Date.now() - startMs.current,
          prompted:   pendingQuality < 3,
          signals:    [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    submRef.current = false;
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setTimeout(() => {
      setFound(0); setSubmitted(false);
      setRound(r => r + 1);
      setPhase("rate"); setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h=>h.correct).length;
    const score   = ROUNDS>0?Math.round((correct/ROUNDS)*100):0;
    if (showNotes) return <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader} startMs={sessionStart.current} onDone={onDone} childName={childName} score={score}/>;
    return (
      <CelebrationScreen correct={correct} total={ROUNDS} childName={childName} worksheetTitle={worksheetTitle}
        onReplay={()=>{ closedRef.current=false; sessionStart.current=Date.now(); setRound(0); setFound(0); setSubmitted(false); setHistory([]); setPhase("rate"); setPendingQuality(null); setShowNotes(false); }}
        onExit={()=>setShowNotes(true)}/>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#f0f4ff",display:"flex",flexDirection:"column"}}>
      <ModeBar icon={wt.icon} title="Find the Differences" childName={childName} color={wt.color} current={round+1} total={ROUNDS} onExit={onDone}/>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"16px 20px",gap:14}}>

        {phase === "rate" && (
          <>
            <div style={{fontSize:13,color:wt.color,fontWeight:700,textAlign:"center"}}>Find 5 differences between the two pictures</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,width:"min(640px,98vw)"}}>
              {["Picture A","Picture B"].map((label,li)=>(
                <div key={li} style={{border:`2px solid ${wt.color}`,borderRadius:12,overflow:"hidden",background:"white"}}>
                  <div style={{background:wt.bg,padding:"6px 12px",fontSize:10,fontWeight:800,color:wt.color,borderBottom:`1px solid ${wt.color}30`}}>{label}</div>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:12,background:"#fafafa",minHeight:160}}>
                    {item ? <img src={resolveUrl(item)} alt="" style={{maxWidth:"100%",maxHeight:200,objectFit:"contain"}}/> : <div style={{fontSize:40,color:"#ddd"}}>🖼️</div>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{background:"white",border:`2px solid ${wt.color}30`,borderRadius:14,padding:"16px 24px",display:"flex",alignItems:"center",gap:20,boxShadow:"0 2px 12px rgba(0,0,0,.06)"}}>
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:44,fontWeight:900,color:wt.color,lineHeight:1}}>{found}</div>
                <div style={{fontSize:10,color:"#8a9ab5",fontWeight:700,textTransform:"uppercase",letterSpacing:0.6,marginTop:4}}>of 5 found</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <button onClick={()=>setFound(f=>Math.min(f+1,5))} disabled={found>=5||submitted}
                  style={{padding:"10px 20px",background:wt.color,color:"white",border:"none",borderRadius:10,fontWeight:800,fontSize:13,cursor:found>=5||submitted?"default":"pointer",opacity:found>=5||submitted?0.4:1}}>
                  ＋ Child Found One
                </button>
                <button onClick={()=>setFound(f=>Math.max(f-1,0))} disabled={found===0||submitted}
                  style={{padding:"10px 20px",background:"#f0f4f8",color:"#8a9ab5",border:"none",borderRadius:10,fontWeight:700,fontSize:13,cursor:found===0||submitted?"default":"pointer",opacity:found===0||submitted?0.4:1}}>
                  − Undo
                </button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {Array.from({length:5}).map((_,i)=>(
                  <div key={i} style={{width:16,height:16,borderRadius:"50%",background:i<found?wt.color:"#e2e8f0",transition:"background .2s"}}/>
                ))}
              </div>
            </div>
            {!submitted && (
              <button onClick={()=>setSubmitted(true)} style={{padding:"12px 36px",background:wt.color,color:"white",border:"none",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer",boxShadow:`0 4px 16px ${wt.color}44`}}>
                Submit — {found} Difference{found!==1?"s":""} Found
              </button>
            )}
            {submitted && <TeacherRating onRate={handleQualityRate} disabled={submRef.current}/>}
          </>
        )}

        {phase === "probe" && (
          <>
            <div style={{fontSize:11,color:wt.color,fontWeight:700,textTransform:"uppercase",letterSpacing:.5}}>
              ✓ Rated — one more question:
            </div>
            <ProbeRound
              probe={probe}
              item={probeItem}
              language={language}
              onRate={handleProbeRate}
              submitting={submRef.current}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETUP SCREEN  (unchanged from original)
// ══════════════════════════════════════════════════════════════════════════════
function SetupScreen({ onStart, getAuthHeader, linkedId }) {
  const { user } = useAuth();
  const [children,   setChildren]   = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [childId,    setChildId]    = useState("");
  const [wsId,       setWsId]       = useState("");
  const [language,   setLanguage]   = useState("en");
  const [loading,    setLoading]    = useState(true);
  const [starting,   setStarting]   = useState(false);
  const [search,     setSearch]     = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [err,        setErr]        = useState("");

  useEffect(()=>{
    (async()=>{
      try {
        const [cd,wd] = await Promise.all([
          apiFetch("GET","/children?limit=200",  null,getAuthHeader()),
          apiFetch("GET","/worksheet?limit=100", null,getAuthHeader()),
        ]);
        const kids = Array.isArray(cd?.children)?cd.children:Array.isArray(cd)?cd:[];
        setChildren(kids);
        setWorksheets(wd?.worksheets||[]);
        if (kids.length===1) setChildId(kids[0]._id);
        if (linkedId) setWsId(linkedId);
      } catch(e){ setErr(e.message); }
      setLoading(false);
    })();
  },[]);

  const filteredWs = worksheets.filter(ws=>{
    const mt = typeFilter==="all"||ws.type===typeFilter;
    const ms = !search||ws.title.toLowerCase().includes(search.toLowerCase());
    return mt&&ms;
  });

  const selectedWs    = worksheets.find(w=>w._id===wsId);
  const selectedChild = children.find(c=>c._id===childId);
  const wt            = WORKSHEET_TYPES.find(t=>t.id===selectedWs?.type);
  const canStart      = childId && wsId;

  const start = async () => {
    if (!canStart) return;
    setStarting(true); setErr("");
    try {
      const data = await apiFetch("GET",`/worksheet/${wsId}`,null,getAuthHeader());
      if (!data.success) throw new Error(data.message||"Failed to load worksheet");
      const full = data.worksheet;
      apiFetch("POST",`/worksheet/${wsId}/use`,null,getAuthHeader()).catch(()=>{});
      const base  = API.replace("/api","");
      const items = (full.items||[]).map(it=>({
        ...it,
        imageUrl: it.imageUrl?.startsWith("/uploads")?`${base}${it.imageUrl}`:it.imageUrl,
      }));
      const sd = await apiFetch("POST","/flashcard-sessions/log",{
        childId, moduleType:"worksheet", moduleRef:wsId, setTitle:full.title,
        sessionNotes:`Started: ${full.type}`, durationMin:0,
        conductorRole: user?.role==="parent"?"parent":"teacher",
        sessionType:   user?.role==="parent"?"parent-led":"teacher-led",
      }, getAuthHeader());
      const sessionId = sd.session?._id || sd._id || null;
      onStart({ child:selectedChild, worksheet:full, items, language, sessionId });
    } catch(e){ setErr(e.message); }
    setStarting(false);
  };

  if (loading) return <div style={{background:"#f0f4ff",minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center"}}><Spinner/></div>;

  return (
    <div style={{background:"#f0f4ff",minHeight:"100vh",padding:"40px 16px",fontFamily:"'Segoe UI',Arial,sans-serif"}}>
      <div style={{maxWidth:540,margin:"0 auto"}}>
        <div style={{marginBottom:28,textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:8}}>📝</div>
          <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,color:"#003366",marginBottom:4}}>Worksheet Activity</div>
          <div style={{fontSize:13,color:"#888"}}>Interactive digital learning · one skill probe per item</div>
        </div>

        {err && <div style={{background:"#FDEDEC",color:"#C0392B",padding:"10px 14px",borderRadius:8,fontSize:12,marginBottom:16,fontWeight:600}}>{err}</div>}

        {/* Child */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Child *</label>
          {children.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:14}}>No children found</div>
            : <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:190,overflowY:"auto"}}>
                {children.map(c=>{
                  const age=c.dob?new Date().getFullYear()-new Date(c.dob).getFullYear():null;
                  const sel=childId===c._id;
                  return (
                    <div key={c._id} onClick={()=>setChildId(c._id)}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",border:`2px solid ${sel?"#003366":"#dce3f5"}`,borderRadius:9,background:sel?"#EBF4FB":"white",cursor:"pointer",transition:"all .15s"}}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:"#003366",display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:11,fontWeight:800,flexShrink:0}}>{(c.name||"?")[0].toUpperCase()}</div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#003366"}}>{c.name}</div>
                        {age&&<div style={{fontSize:10,color:"#888"}}>{age} years old</div>}
                      </div>
                      {sel&&<span style={{color:"#003366",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        {/* Worksheet */}
        <div style={{background:"white",borderRadius:14,padding:18,marginBottom:14,border:"1.5px solid #e0e7f0",boxShadow:"0 1px 4px rgba(0,0,0,.05)"}}>
          <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Worksheet *</label>
          <div style={{display:"flex",gap:8,marginBottom:10}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…"
              style={{flex:1,border:"1.5px solid #dce3f5",borderRadius:8,padding:"7px 11px",fontSize:12,outline:"none"}}/>
            <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}
              style={{border:"1.5px solid #dce3f5",borderRadius:8,padding:"7px 10px",fontSize:12,outline:"none",background:"white"}}>
              <option value="all">All Types</option>
              {WORKSHEET_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          {filteredWs.length===0
            ? <div style={{fontSize:12,color:"#aaa",textAlign:"center",padding:20}}>
                {worksheets.length===0?"No worksheets found. Create one in the Worksheet module first.":"No worksheets match your search."}
              </div>
            : <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:220,overflowY:"auto"}}>
                {filteredWs.map(ws=>{
                  const wtype=WORKSHEET_TYPES.find(t=>t.id===ws.type); const sel=wsId===ws._id;
                  return (
                    <div key={ws._id} onClick={()=>setWsId(ws._id)}
                      style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:12,border:`2px solid ${sel?wtype?.color||"#003366":"#dce3f5"}`,borderRadius:9,background:sel?`${wtype?.color||"#003366"}0d`:"white",cursor:"pointer",transition:"all .15s"}}>
                      <span style={{fontSize:20}}>{wtype?.icon||"📄"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#003366",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ws.title}</div>
                        <div style={{fontSize:10,color:"#888",marginTop:1}}>{wtype?.label} · Used {ws.usedCount||0}×</div>
                      </div>
                      {sel&&<span style={{color:wtype?.color||"#003366",fontWeight:900,fontSize:16}}>✓</span>}
                    </div>
                  );
                })}
              </div>
          }
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0"}}>
            <label style={{display:"block",fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:0.8,marginBottom:8}}>Language</label>
            <select value={language} onChange={e=>setLanguage(e.target.value)} style={{width:"100%",padding:"9px 10px",border:"1.5px solid #dce3f5",borderRadius:8,fontSize:12,outline:"none",background:"white"}}>
              {LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div style={{background:"white",borderRadius:14,padding:16,border:"1.5px solid #e0e7f0",display:"flex",alignItems:"center",justifyContent:"center"}}>
            {wt
              ? <div style={{textAlign:"center"}}>
                  <div style={{fontSize:26,marginBottom:4}}>{wt.icon}</div>
                  <div style={{fontSize:11,fontWeight:700,color:wt.color}}>{wt.label}</div>
                  <div style={{fontSize:9,color:"#aaa",marginTop:2,lineHeight:1.4}}>{wt.desc}</div>
                </div>
              : <div style={{fontSize:11,color:"#aaa",textAlign:"center"}}>Mode preview<br/>after selecting worksheet</div>
            }
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:20}}>
          {WORKSHEET_TYPES.slice(0,5).map(m=>{
            const isActive=selectedWs?.type===m.id;
            return (
              <div key={m.id} style={{padding:"12px 8px",background:isActive?m.bg:"white",border:`2px solid ${isActive?m.color:"#dce3f5"}`,borderRadius:12,textAlign:"center",transition:"all .15s",opacity:isActive?1:0.55}}>
                <div style={{fontSize:20,marginBottom:4}}>{m.icon}</div>
                <div style={{fontSize:10,fontWeight:700,color:isActive?m.color:"#555"}}>{m.label}</div>
                <div style={{fontSize:9,color:"#aaa",marginTop:2,lineHeight:1.4}}>{m.desc}</div>
              </div>
            );
          })}
        </div>

        {selectedWs && wt && (
          <div style={{background:"#EBF4FB",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#1A4A6E",fontWeight:600,display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontSize:16}}>ℹ️</span>
            <span>{(selectedWs.items?.length||0)} items · {wt.desc} · one skill probe per item</span>
          </div>
        )}

        <button onClick={start} disabled={!canStart||starting}
          style={{width:"100%",padding:"15px",background:canStart?"#003366":"#ccc",color:"white",border:"none",borderRadius:12,fontWeight:800,fontSize:15,cursor:canStart?"pointer":"not-allowed",boxShadow:canStart?"0 6px 24px rgba(0,51,102,.3)":"none"}}>
          {starting?"Loading…":`▶ Start ${wt?.label||"Activity"}`}
        </button>
        <div style={{textAlign:"center",fontSize:11,color:"#aaa",marginTop:12}}>Child responses tracked · one skill probe per round</div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT  (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
export default function WorksheetLearning() {
  const { getAuthHeader } = useAuth();
  const { state: navState } = useLocation();
  const [phase,    setPhase]    = useState("setup");
  const [activity, setActivity] = useState(null);

  const launch = (data) => { setActivity(data); setPhase("play"); };
  const exit   = async () => {
    if (navState?.workplanId && navState?.entryId) {
      try {
        await fetch(`${API}/workplan/${navState.workplanId}/entry/${navState.entryId}`, {
          method:"PATCH", headers:{"Content-Type":"application/json",...getAuthHeader()},
          body: JSON.stringify({status:"done"}),
        });
      } catch {}
    }
    setActivity(null); setPhase("setup");
  };

  if (phase==="setup") return <SetupScreen getAuthHeader={getAuthHeader} onStart={launch} linkedId={navState?.linkedId}/>;

  if (phase==="play" && activity) {
    const { child, worksheet, items, language, sessionId } = activity;
    const childName      = child?.name||"";
    const worksheetTitle = worksheet?.title||"";
    const common = { items, language, sessionId, getAuthHeader, childName, worksheetTitle, onDone:exit };

    switch (worksheet.type) {
      case "odd-one-out":     return <OddOneOutMode  {...common}/>;
      case "match-following": return <MatchMode       {...common}/>;
      case "sequence-order":  return <SequenceMode    {...common}/>;
      case "count-items":     return <CountMode       {...common}/>;
      case "find-differences":return <DiffMode        {...common}/>;
      default:
        return (
          <div style={{padding:40,textAlign:"center",color:"#8a9ab5",fontFamily:"'Segoe UI',Arial,sans-serif"}}>
            <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
            Unknown worksheet type: <strong>{worksheet.type}</strong><br/>
            <button onClick={exit} style={{marginTop:20,padding:"10px 24px",background:"#003366",color:"white",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>← Back</button>
          </div>
        );
    }
  }

  return null;
}